import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect, useCallback } from 'react';
import { useRoleLabels } from '../contexts/RoleLabelsContext';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { usePermissions } from '../hooks/usePermissions';

const token = () => localStorage.getItem('token');

const BROKER_LEVELS = [
  { value: 'owner',   label: 'Broker Company' },
  { value: 'manager', label: 'Broker Manager' },
  { value: 'adviser', label: 'Broker Adviser' },
];

const LEVEL_COLOURS = {
  owner:   { bg: '#0e1b2c', color: '#fff' },
  manager: { bg: '#dbeafe', color: '#1e40af' },
  adviser: { bg: '#EDF2F7', color: '#4a5568' },
};

function LevelBadge({ level }) {
  const c = LEVEL_COLOURS[level] || LEVEL_COLOURS.adviser;
  const { labelForTier } = useRoleLabels();
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      background: c.bg, color: c.color }}>
      {labelForTier('broker', level)}
    </span>
  );
}

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      background: active ? '#d1fae5' : '#fee2e2',
      color: active ? '#065f46' : '#991b1b' }}>
      {status}
    </span>
  );
}

// ── User edit modal ───────────────────────────────────────────
function EditModal({ user, managers, onSave, onClose }) {
  const [form, setForm] = useState({
    name:         user.name         || '',
    email:        user.email        || '',
    phone:        user.phone        || '',
    broker_level: user.broker_level || 'adviser',
    manager_id:   user.manager_id   || '',
    status:       user.status       || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await onSave(user.id, {
        name:         form.name,
        phone:        form.phone || null,
        broker_level: form.broker_level,
        manager_id:   form.broker_level === 'adviser' ? (form.manager_id || null) : null,
        status:       form.status,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const availableManagers = managers.filter(m => m.id !== user.id && m.broker_level === 'manager');

  const s = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal:   { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    head:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #EDF2F7' },
    body:    { padding: '24px' },
    label:   { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
    input:   { width: '100%', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
    group:   { marginBottom: 16 },
    grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    foot:    { padding: '16px 24px', borderTop: '1px solid #EDF2F7', display: 'flex', gap: 10, justifyContent: 'flex-end' },
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.head}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, color: '#0e1b2c' }}>Edit User</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#718096' }}>{user.email}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#718096', lineHeight: 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={s.body}>
            <div style={{ ...s.grid2, marginBottom: 16 }}>
              <div>
                <label style={s.label}>Full Name</label>
                <input name="name" value={form.name} onChange={handleChange} style={s.input} required />
              </div>
              <div>
                <label style={s.label}>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} style={s.input} placeholder="Optional" />
              </div>
            </div>

            <div style={{ ...s.grid2, marginBottom: 16 }}>
              <div>
                <label style={s.label}>Broker Level</label>
                <select name="broker_level" value={form.broker_level} onChange={handleChange} style={s.input}>
                  {BROKER_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Status</label>
                <select name="status" value={form.status} onChange={handleChange} style={s.input}>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            {/* Manager dropdown — only shown for advisers */}
            {form.broker_level === 'adviser' && (
              <div style={s.group}>
                <label style={s.label}>Assigned Manager <span style={{ fontWeight: 400, color: '#a0aec0' }}>(optional)</span></label>
                <select name="manager_id" value={form.manager_id} onChange={handleChange} style={s.input}>
                  <option value="">— No manager assigned —</option>
                  {availableManagers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                {availableManagers.length === 0 && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a0aec0' }}>No managers in this company yet.</p>
                )}
              </div>
            )}

            {error && (
              <div style={{ background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 8, padding: '10px 12px', color: '#c53030', fontSize: 13 }}>
                ⚠️ {error}
              </div>
            )}
          </div>

          <div style={s.foot}>
            <button type="button" onClick={onClose}
              style={{ background: '#fff', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 20px', fontSize: 14, cursor: 'pointer', color: '#718096' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 24px', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Create user modal ─────────────────────────────────────────
function CreateModal({ companyId, managers, onCreated, onClose }) {
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    broker_level: 'adviser', manager_id: '', role: 'broker',
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [showPass, setShowPass] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          broker_company_id: companyId,
          manager_id: form.broker_level === 'adviser' ? (form.manager_id || null) : null,
          phone: form.phone || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create user');
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const availableManagers = managers.filter(m => m.broker_level === 'manager');

  const s = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal:   { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' },
    head:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #EDF2F7', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
    body:    { padding: '24px' },
    label:   { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
    input:   { width: '100%', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
    group:   { marginBottom: 16 },
    grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 },
    foot:    { padding: '16px 24px', borderTop: '1px solid #EDF2F7', display: 'flex', gap: 10, justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: '#fff' },
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.head}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#0e1b2c' }}>Add Team Member</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#718096' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={s.body}>
            <div style={s.grid2}>
              <div>
                <label style={s.label}>Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} style={s.input} required />
              </div>
              <div>
                <label style={s.label}>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} style={s.input} placeholder="Optional" />
              </div>
            </div>

            <div style={s.group}>
              <label style={s.label}>Email *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} style={s.input} required />
            </div>

            <div style={s.group}>
              <label style={s.label}>Password *</label>
              <div style={{ position: 'relative' }}>
                <input name="password" type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={handleChange} style={{ ...s.input, paddingRight: 80 }} required minLength={8} />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 12, color: '#718096', cursor: 'pointer' }}>
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#a0aec0' }}>Minimum 8 characters</p>
            </div>

            <div style={s.grid2}>
              <div>
                <label style={s.label}>Broker Level *</label>
                <select name="broker_level" value={form.broker_level} onChange={handleChange} style={s.input}>
                  {BROKER_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>System Role *</label>
                <select name="role" value={form.role} onChange={handleChange} style={s.input}>
                  <option value="broker">Broker</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {form.broker_level === 'adviser' && (
              <div style={s.group}>
                <label style={s.label}>Assigned Manager <span style={{ fontWeight: 400, color: '#a0aec0' }}>(optional)</span></label>
                <select name="manager_id" value={form.manager_id} onChange={handleChange} style={s.input}>
                  <option value="">— No manager assigned —</option>
                  {availableManagers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}

            {error && (
              <div style={{ background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 8, padding: '10px 12px', color: '#c53030', fontSize: 13 }}>
                ⚠️ {error}
              </div>
            )}
          </div>

          <div style={s.foot}>
            <button type="button" onClick={onClose}
              style={{ background: '#fff', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 20px', fontSize: 14, cursor: 'pointer', color: '#718096' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 24px', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reset password modal ──────────────────────────────────────
function ResetPasswordModal({ user, onClose }) {
  const [password, setPassword]   = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [success, setSuccess]     = useState(false);
  const [error, setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`/api/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: password }),
      });
      if (!res.ok) throw new Error('Failed to reset password');
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
  const modal   = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: 24 };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, color: '#0e1b2c' }}>Reset Password</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#718096' }}>{user.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#718096' }}>✕</button>
        </div>

        {success ? (
          <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '12px 16px', color: '#065f46', fontSize: 14 }}>
            ✓ Password reset successfully
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>New Password *</label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 12px', paddingRight: 60, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  required minLength={8} autoFocus />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 12, color: '#718096', cursor: 'pointer' }}>
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#a0aec0' }}>Minimum 8 characters</p>
            </div>
            {error && <div style={{ color: '#c53030', fontSize: 13, marginBottom: 12 }}>⚠️ {error}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose}
                style={{ background: '#fff', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 18px', fontSize: 14, cursor: 'pointer', color: '#718096' }}>
                Cancel
              </button>
              <button type="submit" disabled={saving}
                style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function UserManagement() {
  const mobile = useMobile();
  const { companyId } = useParams();
  const navigate      = useNavigate();
  const perms         = usePermissions();

  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [editingUser, setEditingUser]   = useState(null);
  const [showCreate, setShowCreate]     = useState(false);
  const [resetUser, setResetUser]       = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/users/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSave = async (userId, body) => {
    const res = await fetch(`/api/users/${userId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Save failed');
    await loadUsers();
  };

  const handleDelete = async (userId) => {
    try {
      const res = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) { const data = await res.json().catch(() => ({})); throw new Error(data.error || 'Failed to archive user'); }
      setDeleteConfirm(null);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const managers = users.filter(u => u.broker_level === 'manager');

  const ownerCount   = users.filter(u => u.broker_level === 'owner').length;
  const managerCount = users.filter(u => u.broker_level === 'manager').length;
  const adviserCount = users.filter(u => u.broker_level === 'adviser').length;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <Sidebar />
      <main style={{ flex: 1, marginLeft: mobile ? 0 : 70, padding: '32px 40px', width: mobile ? '100%' : 'calc(100% - 70px)', boxSizing: 'border-box' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <button onClick={() => navigate(-1)}
                style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#718096' }}>
                ← Back
              </button>
              <h1 style={{ margin: 0, fontSize: 24, color: '#0e1b2c' }}>Team Members</h1>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#718096' }}>
              {users.length} users · {ownerCount} owner{ownerCount !== 1 ? 's' : ''} · {managerCount} manager{managerCount !== 1 ? 's' : ''} · {adviserCount} adviser{adviserCount !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={() => setShowCreate(true)}
            style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            + Add User
          </button>
        </div>

        {error && (
          <div style={{ background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 8, padding: '10px 16px', color: '#c53030', marginBottom: 16, fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
            ⚠️ {error}
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c53030' }}>✕</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#718096' }}>Loading users…</div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#718096' }}>
            <p>No users yet.</p>
            <button onClick={() => setShowCreate(true)}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>
              Add First User
            </button>
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 110px 100px 80px 160px', gap: 0, padding: '10px 20px', background: '#F7FAFC', borderBottom: '1px solid #EDF2F7', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a0aec0' }}>
              <span>Name</span>
              <span>Manager</span>
              <span>Level</span>
              <span>Status</span>
              <span></span>
              <span style={{ textAlign: 'right' }}>Actions</span>
            </div>

            {users.map((user, idx) => (
              <div key={user.id}
                style={{ display: 'grid', gridTemplateColumns: '1fr 200px 110px 100px 80px 160px', gap: 0, padding: '14px 20px', borderBottom: idx < users.length - 1 ? '1px solid #F7FAFC' : 'none', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>{user.email}</div>
                  {user.phone && <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 1 }}>{user.phone}</div>}
                </div>

                <div style={{ fontSize: 13, color: '#4a5568' }}>
                  {user.manager_name
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                        {user.manager_name}
                      </span>
                    : <span style={{ color: '#CBD5E0' }}>—</span>
                  }
                </div>

                <div>
                  {user.introducer_id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#92400e', textTransform: 'capitalize' }}>
                        Introducer
                      </span>
                      <span style={{ fontSize: 10, color: '#718096', fontStyle: 'italic' }}>
                        {user.introducer_level || 'adviser'}
                      </span>
                    </div>
                  ) : (
                    <LevelBadge level={user.broker_level} />
                  )}
                </div>
                <div><StatusBadge status={user.status} /></div>
                <div></div>

                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button onClick={() => setEditingUser(user)}
                    style={{ background: 'none', border: '1px solid #10b981', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#10b981' }}>
                    Edit
                  </button>
                  <button onClick={() => setResetUser(user)}
                    style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#718096' }}>
                    Password
                  </button>
                  {perms.isPlatformOwner && (
                    <button onClick={() => setDeleteConfirm(user)}
                      style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: '#ef4444' }}>
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 380, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 8px', color: '#0e1b2c' }}>Delete User?</h3>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: '#4a5568' }}>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ background: '#fff', border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 18px', fontSize: 14, cursor: 'pointer', color: '#718096' }}>
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm.id)}
                  style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {editingUser && (
          <EditModal user={editingUser} managers={managers} onSave={handleSave} onClose={() => setEditingUser(null)} />
        )}
        {showCreate && (
          <CreateModal companyId={companyId} managers={managers} onCreated={loadUsers} onClose={() => setShowCreate(false)} />
        )}
        {resetUser && (
          <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />
        )}

      </main>
    </div>
  );
}
